import { useMemo } from 'react';
import { agentTokenInstructions, protocolSection } from '@shared/brief.ts';
import { functionsUrl } from '@/lib/supabase.ts';
import { navigate, type ProtocolVariant } from '@/lib/router.ts';
import { Page, Card } from './Page.tsx';
import { Button, SectionLabel, useToast } from '@/components/ui.tsx';

/**
 * The protocol, as documentation — and the one page in the app that does not
 * need a session.
 *
 * Nothing depends on anybody opening this: a dispatch already carries the
 * concrete callback for its own task, and an intake or a freshly minted agent
 * token carries a link here rather than a copy of this text. This page is
 * where that link points — for a person deciding what to add in Settings, and
 * for a routine that has nothing but a CLAUDE.md pointing at
 * `<this app>/protocol` and no session to read it with.
 *
 * That last reader is why App.tsx renders this one route before the sign-in
 * gate, and why it reads `functionsUrl` from the build-time constant rather
 * than the authenticated `/api/config` — both are public by construction (the
 * base URL is not a secret; RLS guards what actually matters), so nothing here
 * needs to wait on `useRigg()`.
 *
 * Rendered from the same functions that build every real brief and every real
 * instructions payload, rather than a copy of them, so a documentation page
 * that quietly describes an older protocol is not a failure mode this can
 * have. Placeholders like `<task id>` stand where each task's or routine's
 * real values are substituted in — *Copy brief* on any task, or *Mint token*
 * on any routine, shows that version.
 */
export function ProtocolScreen({
  variant,
  standalone,
}: {
  variant: ProtocolVariant;
  standalone?: boolean;
}) {
  const content = <ProtocolContent variant={variant} />;
  if (!standalone) return content;

  return (
    <div className="min-h-screen min-h-dvh bg-app">
      <PublicHeader />
      {content}
    </div>
  );
}

function PublicHeader() {
  return (
    <div className="flex items-center gap-2.5 border-b border-line px-4 py-3 sm:px-6 lg:px-8">
      <button
        onClick={() => navigate('/')}
        className="rg-focus flex min-w-0 items-center gap-2.5 rounded px-1.5 text-left"
      >
        <span className="size-[20px] shrink-0 rounded-md bg-accent" aria-hidden />
        <span className="text-[15px] font-bold tracking-[-0.01em] text-hi">Rigg</span>
      </button>
      <span className="flex-1" />
      <button
        onClick={() => navigate('/')}
        className="rg-focus rounded px-2 py-1 text-[13px] font-semibold text-mute hover:text-hi2"
      >
        Sign in
      </button>
    </div>
  );
}

function ProtocolContent({ variant }: { variant: ProtocolVariant }) {
  const { notify } = useToast();

  const origin = useMemo(
    () => (typeof window === 'undefined' ? '' : window.location.origin.replace(/\/+$/, '')),
    [],
  );
  const appUrl = origin || '<your rigg app url>';
  const claudeMdSnippet = `Complete the task you are assigned. Download and follow these instructions\nfor reporting: ${appUrl}/protocol`;

  const task = useMemo(
    () =>
      protocolSection({
        functionsUrl,
        taskId: '<task id>',
        token: '<the task callback token>',
        autoMerge: false,
      }),
    [],
  );

  const agent = useMemo(
    () =>
      agentTokenInstructions({
        functionsUrl,
        appUrl,
        token: '<your agent token>',
        agentName: '<your routine>',
        projectName: '<your project>',
        categories: [],
      }),
    [appUrl],
  );

  const text = variant === 'agent' ? agent : task;

  const copy = async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value);
      notify(`${what} copied`);
    } catch {
      notify('Could not copy');
    }
  };

  return (
    <Page
      title="Agent protocol"
      wide
      blurb={
        <>
          Reference, and public — no sign-in needed. A dispatch already carries the concrete
          callback for its own task, so nothing here needs doing; this page exists so a person
          can read the rules without minting a token, and so a routine following a link from a
          project's <code className="font-mono text-[12px]">CLAUDE.md</code> has somewhere to
          land. Generated from the same code the API validates against, so it cannot drift from
          what routines are actually told.
        </>
      }
      actions={
        <Button
          tone="primary"
          onClick={async () => {
            await navigator.clipboard.writeText(text);
            notify('Copied');
          }}
        >
          Copy
        </Button>
      }
    >
      <SectionLabel className="mb-2.5">For a CLAUDE.md</SectionLabel>
      <Card className="mb-6">
        <p className="mb-3 text-[12.5px] leading-relaxed text-mute">
          Point a project's own instructions here instead of pasting the protocol into them — this
          page is the one place it can change without every project going stale.
        </p>
        <div className="flex items-start gap-2">
          <pre className="min-w-0 flex-1 overflow-x-auto rounded-xl border border-line bg-brief p-3.5 font-mono text-[12px] leading-relaxed whitespace-pre-wrap text-t6">
            {claudeMdSnippet}
          </pre>
          <Button size="sm" onClick={() => void copy(claudeMdSnippet, 'Snippet')}>
            Copy
          </Button>
        </div>
      </Card>

      <SectionLabel className="mb-2.5">The endpoints</SectionLabel>
      <Card className="mb-6">
        <div className="flex flex-col gap-2 font-mono text-[12px]">
          <Endpoint method="POST" path="/agent/tasks/:id/report" note="the workhorse — state, activity, links, images" />
          <Endpoint method="GET" path="/agent/tasks/:id" note="poll: approved? sent back? what did they say?" />
          <Endpoint method="POST" path="/agent/tasks/:id/comments" note="ask a question, or answer one" />
          <Endpoint method="POST" path="/agent/tasks" note="open a task from scratch (agent token)" />
          <Endpoint method="GET" path="/agent/tasks" note="everything assigned to me (agent token)" />
          <Endpoint method="POST" path="/agent/uploads" note="a signed URL to put a screenshot" />
          <Endpoint method="GET" path="/agent/instructions" note="this page, as plain text — send a token for the real values" />
          <Endpoint method="GET" path="/agent/whoami" note="what is this token worth?" />
        </div>
        <p className="mt-3.5 border-t border-line pt-3 text-[12.5px] leading-relaxed text-mute">
          Base URL <code className="font-mono text-t7">{functionsUrl}</code>. Every call carries{' '}
          <code className="font-mono text-t7">Authorization: Bearer &lt;token&gt;</code>. A{' '}
          <strong className="text-t2">task token</strong> is worth exactly one task; an{' '}
          <strong className="text-t2">agent token</strong> is worth one routine's tasks plus the
          right to open new ones. Only the SHA-256 of each is stored, so a lost token is replaced,
          never recovered.
        </p>
      </Card>

      <div className="mb-4 flex gap-2">
        <Button
          size="sm"
          tone={variant === 'task' ? 'primary' : 'ghost'}
          onClick={() => navigate('/protocol')}
        >
          Task token — reporting
        </Button>
        <Button
          size="sm"
          tone={variant === 'agent' ? 'primary' : 'ghost'}
          onClick={() => navigate('/protocol/agent')}
        >
          Agent token — standing
        </Button>
      </div>
      <p className="mb-4 text-[12.5px] leading-relaxed text-mute">
        {variant === 'agent'
          ? "What a routine with its own permanent token can do — open tasks nobody asked for, register work it already started, see what's assigned to it. Once it holds a task-scoped callback token, the reporting rules on the other tab apply."
          : 'What a routine dispatched by Rigg is told: how to report on the one task it was given. This is also what a self-registered task gets once it has a callback token.'}
      </p>

      <pre className="overflow-x-auto rounded-2xl border border-line bg-brief p-5 font-mono text-[12px] leading-relaxed whitespace-pre-wrap text-t6">
        {text}
      </pre>
    </Page>
  );
}

function Endpoint({ method, path, note }: { method: string; path: string; note: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="w-12 shrink-0 font-semibold text-accent">{method}</span>
      <span className="text-t1">{path}</span>
      <span className="font-sans text-[12px] text-mute">{note}</span>
    </div>
  );
}
